import React from 'react';
import { XCircleIcon } from './Icons';

interface HelpModalProps {
  onClose: () => void;
}

const HelpSection: React.FC<{ title: string, children: React.ReactNode }> = ({ title, children }) => (
    <div className="mb-6">
        <h3 className="text-xl font-bold text-blue-400 border-b border-slate-700 pb-2 mb-3">{title}</h3>
        <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
            {children}
        </div>
    </div>
);

const HelpModal: React.FC<HelpModalProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in no-print" role="dialog" aria-modal="true">
      <div className="bg-slate-800 rounded-lg shadow-2xl w-full max-w-3xl h-full max-h-[90vh] flex flex-col border border-slate-700">
        <header className="flex-shrink-0 flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-2xl font-bold text-white">App Guide & Help</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <XCircleIcon className="w-8 h-8" />
            <span className="sr-only">Close help</span>
          </button>
        </header>
        
        <main className="flex-grow p-6 overflow-y-auto">
          <HelpSection title="General Navigation & Icons">
            <p>This app helps you track flight operations in real-time. Use the tabs at the top to switch between different screens.</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Header Icons:</strong> Quickly access your Profile (for password, PIN, 2FA), start a New Session (clears current data after saving it as a report), and Logout.</li>
                <li><strong className="text-white">News Bar:</strong> The scrolling red bar at the top displays important announcements and key milestones for all users to see.</li>
                <li><strong className="text-white">Footer:</strong> Shows the current time, a countdown to the ETD (Estimated Time of Departure), and the status of data synchronization with the server (<span className="text-green-400">SEND</span> / <span className="text-red-400">RECV</span>).</li>
            </ul>
          </HelpSection>

          <HelpSection title="Installing the App (PWA)">
            <p>For the best experience, install this app on your device. This allows for quick access from your home screen and improved offline functionality, especially on iOS.</p>
            <ul className="list-disc list-inside space-y-2 pl-2 mt-2">
                 <li>
                    <strong className="text-white">iOS (iPhone/iPad):</strong> Tap the "Share" button in Safari's toolbar, then scroll down and select "Add to Home Screen".
                </li>
                <li>
                    <strong className="text-white">Android/Chrome:</strong> Look for an "Install" button in the address bar or tap the three-dot menu and select "Install app" or "Add to Home screen".
                </li>
                <li>
                    <strong className="text-white">Desktop (Chrome/Edge):</strong> An "Install" icon (usually a computer with a down arrow) will appear in the address bar. Click it to install the app.
                </li>
            </ul>
          </HelpSection>

          <HelpSection title="Multi-Flight Management">
            <p>When enabled by an administrator, you can manage up to three flights simultaneously.</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Flight Tabs:</strong> A set of tabs will appear below the main header, one for each active flight. Click a tab to switch context instantly.</li>
                <li><strong className="text-white">Adding a Flight:</strong> Use the "+" button in the flight tab bar to start a new flight session in an available slot.</li>
                <li><strong className="text-white">Ending a Flight Session:</strong> Click the 'x' on a flight tab to end that session. This will save the flight data as a report and remove it from the active list.</li>
            </ul>
          </HelpSection>

           <HelpSection title="My Profile & Security Settings">
                <p>Access your profile by clicking the profile icon in the header. Here you can enhance your account's security and manage notifications.</p>
                <ul className="list-disc list-inside space-y-2 pl-2">
                    <li><strong className="text-white">Change Password:</strong> Update your login password.</li>
                    <li><strong className="text-white">Security PIN:</strong> Set a 4-digit PIN for faster, secure logins after your initial password entry.</li>
                    <li><strong className="text-white">Authenticator App (2FA):</strong> For the highest level of security, link an authenticator app (like Google Authenticator) to your account. You'll use a code from the app to log in.</li>
                    <li><strong className="text-white">Push Notifications:</strong> Enable or disable push notifications directly on your device to receive important alerts.</li>
                </ul>
            </HelpSection>

          <HelpSection title="Overview Screen">
            <p>This screen provides a high-level summary of the entire flight operation.</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Progress Bar:</strong> Visualizes the flight's progress through all stations. Completed stations are blue, the current station pulses with a glow, and upcoming stations are grey. During boarding, relevant stations turn green.</li>
                <li><strong className="text-white">Immigration Status:</strong> Shows whether immigration has been approved for the flight.</li>
                <li><strong className="text-white">Key Figures:</strong> Displays booking numbers, ETD, and important milestones like Crew Arrival and Doors Closed times.</li>
            </ul>
          </HelpSection>
          
          <HelpSection title="Crew Movement Screen">
            <p>This is the main screen for logging operational timestamps.</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Logging Time:</strong> Tap the <strong className="text-blue-400">Crew</strong> or <strong className="text-amber-400">Pilot</strong> icon for a specific station to record the current time. The icon will turn blue or amber, respectively, and display the recorded time below it. Tapping again will overwrite the previous time.</li>
                <li><strong className="text-white">Paired Arrivals:</strong> Use the "Crew & Pilots Arrived Together" checkbox if both groups are moving together. When checked, tapping one icon will automatically log the same time for the other.</li>
                <li><strong className="text-white">Flight Setup:</strong> Select the Flight Number and Date at the top of the screen to start a new report.</li>
                <li><strong className="text-white">Capture Document:</strong> Use your device's camera to take a picture of a document and attach it to the report.</li>
            </ul>
          </HelpSection>

          <HelpSection title="Counter Distribution Screen">
            <p>Manage passenger figures and staff assignments for check-in counters and gates.</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Editable Fields:</strong> All fields on this screen are editable. Simply tap and type to update booking figures, staff names at counters, or gate assignments. Changes are saved automatically and are visible to all other users in real-time.</li>
                <li><strong className="text-white">Auto-Calculation:</strong> The "Total Passengers" fields are calculated automatically based on the C, M, and Inf (Infant) numbers you enter.</li>
            </ul>
          </HelpSection>
          
          <HelpSection title="Security Screen">
            <p>Record passengers who have been selected for the security scan (ETD Sheet).</p>
             <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Scan Target:</strong> The app calculates the target number of scans based on a percentage (set by Admins) of the total passengers.</li>
                <li><strong className="text-white">Scan Boarding Pass:</strong> Opens your device's camera to scan the PDF417 barcode on a boarding pass. It will automatically extract the passenger's name and seat number.</li>
                <li><strong className="text-white">Add Manually:</strong> If a scan fails or is not possible, use this option to type in the passenger's details. The data is saved to the current flight's report.</li>
            </ul>
          </HelpSection>

          <HelpSection title="Reports Screen">
            <p>View, export, and manage all past flight reports.</p>
             <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">Select a Report:</strong> Choose a flight from the list on the left to view its detailed report.</li>
                <li><strong className="text-white">Export & Share:</strong> Use the icons at the top to Delete (Admins/Supervisors only), export as a <strong className="text-green-400">PDF</strong>, send via <strong className="text-sky-400">Email</strong>, or <strong className="text-blue-400">Print</strong> the report.</li>
            </ul>
          </HelpSection>
          
           <HelpSection title="Admin Panel (Admins Only)">
            <p>Manage users, system settings, and application assets.</p>
             <ul className="list-disc list-inside space-y-2 pl-2">
                <li><strong className="text-white">User Management:</strong> Create, delete, and manage user roles and passwords.</li>
                <li><strong className="text-white">System Settings:</strong> Control app-wide settings like registration status, authorization codes for new users, and the list of available flight numbers.</li>
                <li><strong className="text-white">App Dependencies:</strong> Customize the app by uploading your own logos and icons using the Icon Wizard.</li>
                <li><strong className="text-white">History Logs:</strong> View detailed logs of user logins and when new flight sessions were started.</li>
             </ul>
          </HelpSection>

        </main>
        
        <footer className="flex-shrink-0 p-4 border-t border-slate-700 text-right">
            <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors">
              Close
            </button>
        </footer>
      </div>
    </div>
  );
};

export default HelpModal;